import { CATALOG_FILTER_TYPES } from '~/constants/catalogFilterTypes';
import type { ActiveFilter, Filter, FilterOption } from '~/types/Catalog';
import {
  getRangeFilterDefinitions,
  getRangeFilterMetadataKey,
  sanitizeRangeFilterQuery,
  type RangeFilterMetadata,
} from '~/utils/rangeFilter';

export function useCatalogFilters(filters: Filter[]) {
  const route = useRoute();
  const activeFilters = ref<ActiveFilter[]>([]);
  const rangeMetadata = useState<RangeFilterMetadata | null>('catalog-range-filter-metadata', () => null);

  rangeMetadata.value = {
    key: getRangeFilterMetadataKey(route.path, route.query),
    filters: getRangeFilterDefinitions(filters ?? []),
  };

  function getActiveFiltersPillsFromQuery() {
    const prevActiveFilters = JSON.parse(JSON.stringify(activeFilters.value));

    activeFilters.value = [];

    if (!filters) return;

    for (const [k, v] of Object.entries(sanitizeRangeFilterQuery(route.query, filters).query)) {
      const skippedQueryKeys = ['filter[search]'];

      if (skippedQueryKeys.includes(k) || !k.startsWith('filter[')) continue;

      const key = k.replace(/filter\[(.*?)]/, '$1');
      const filter = filters.find((f) => f.slug === key);

      if (!filter) continue;

      if (filter.title === 'БазовыйЦвет') filter.title = 'Цвет'; // костылик

      if (typeof v === 'string') {
        if (filter.type === CATALOG_FILTER_TYPES.RANGE) {
          const rangeValues = v.replace(';', ' – ');

          activeFilters.value.push({
            filterTitle: filter.title,
            filterSlug: filter.slug,
            valueText: filter.slug === 'price' ? `${rangeValues} ₽` : rangeValues,
          });
        } else if (filter.type === CATALOG_FILTER_TYPES.CHECKBOX) {
          const checkboxValues = v.split(';').map((slug) => {
            const text =
              filters?.find((f) => f.slug === key)?.options.find((option) => option.value === slug)?.text ??
              prevActiveFilters.find((f: ActiveFilter) => f.valueSlug === slug)?.valueText ??
              slug.toUpperCase();

            return { slug, text };
          });

          for (const value of checkboxValues) {
            activeFilters.value.push({
              filterTitle: filter.title,
              filterSlug: filter.slug,
              valueSlug: value.slug,
              valueText: value.text,
            });
          }
        } else if (filter.type === CATALOG_FILTER_TYPES.RADIO) {
          activeFilters.value.push({
            filterTitle: filter.title,
            filterSlug: filter.slug,
            valueSlug: v,
            valueText: filters?.find((f) => f.slug === key)?.options.find((option) => option.value === v)?.text || v,
          });
        } else if (filter.type === CATALOG_FILTER_TYPES.NUMERIC) {
          activeFilters.value.push({
            filterTitle: filter.title,
            filterSlug: filter.slug,
            valueSlug: v,
            valueText: v,
          });
        }
      }
    }

    activeFilters.value.sort((a, b) => (a.filterTitle > b.filterTitle ? 1 : -1));
  }

  watch(
    () => route.query,
    () => getActiveFiltersPillsFromQuery(),
    { immediate: true },
  );

  const fastCategoryFilters = computed<FilterOption[]>(() => {
    const categoryFilter = filters?.find((f) => f.slug === 'categories');

    return categoryFilter
      ? categoryFilter.options
          .filter((o) => o.fast_filter)
          .sort((a, b) => (a.fast_filter || 0) - (b.fast_filter || 0))
          .filter((o) => !activeFilters.value.map((f) => f.valueSlug).includes(o.value as string))
      : [];
  });

  const fastPropertyFilter = computed(() => getCatalogFastPropertyFilter(filters ?? []));

  return {
    activeFilters,
    fastCategoryFilters,
    fastPropertyFilter,
  };
}
